import cloneDeep from 'lodash/cloneDeep';

import { type Activity, CDFIDMViewMigrator } from '@cognite/apm-client';
import type { Filters, Sort } from '@cognite/fdm-client';

import { activityIDMMigratorContext } from './activity-idm-migrator.context';
import type { ActivityViewMigrator } from './types';

export class ActivityIDMMigrator
  extends CDFIDMViewMigrator
  implements ActivityViewMigrator
{
  /** Instance space where mainAsset (asset) relation is resolved. Defaults to activity space if not provided. */
  private readonly assetSpace: string;

  constructor(space: string, assetSpace?: string) {
    super(space);
    this.assetSpace = assetSpace ?? space;
  }

  mapDataOutward = (data: Record<string, any>): Activity => {
    return {
      ...data,
      externalId: data.externalId,
      title: data.name,
      assetExternalId: data.mainAsset?.externalId,
      id: data.sourceId,
      startTime: data.scheduledStartTime,
      endTime: data.scheduledEndTime,
      actualStartTime: data.startTime,
      actualEndTime: data.endTime,
    };
  };
  mapFiltersInward = (filters: Filters): Filters => {
    const newFilters = cloneDeep(filters);
    Object.keys(filters).forEach((key) => {
      if (key === 'and') {
        newFilters.and = filters.and!.map((f) => this.mapFiltersInward(f));
      }
      if (key === 'or') {
        newFilters.or = filters.or!.map((f) => this.mapFiltersInward(f));
      }
      if (key === 'not') {
        newFilters.not = this.mapFiltersInward(newFilters.not!);
      }

      if (key === 'equals') {
        if (filters.equals?.property === 'rootLocation') {
          newFilters.equals = undefined;
          activityIDMMigratorContext.captureMessage(
            'Removed filter for "rootLocation" since CogniteMaintenanceOrder does not have "rootLocation" property',
            {
              level: 'warning',
              tags: {
                component: 'ActivityIDMMigrator',
                operation: 'mapFiltersInward',
              },
            }
          );
        }
        if (filters.equals?.property === 'assetExternalId') {
          newFilters.equals = undefined;
          newFilters.directRelationFilter = {
            mainAsset: {
              equals: { property: 'externalId', eq: String(filters.equals.eq) },
            },
          };
          newFilters.directRelationInstanceSpace = this.assetSpace;
        }
        if (filters.equals?.property === 'source') {
          newFilters.equals = undefined;
          activityIDMMigratorContext.captureMessage(
            'Removed filter for "source" since CogniteMaintenanceOrder has source as CogniteSourceSystem and filtering is not yet supported',
            {
              level: 'warning',
              tags: {
                component: 'ActivityIDMMigrator',
                operation: 'mapFiltersInward',
              },
            }
          );
        }
      }
      if (key === 'in') {
        if (filters.in?.property === 'rootLocation') {
          newFilters.in = undefined;
          activityIDMMigratorContext.captureMessage(
            'Removed filter for "rootLocation" since CogniteMaintenanceOrder does not have "rootLocation" property',
            {
              level: 'warning',
              tags: {
                component: 'ActivityIDMMigrator',
                operation: 'mapFiltersInward',
              },
            }
          );
        }
      }
      if (key === 'isNull') {
        if (filters.isNull?.property === 'cloneOf') {
          newFilters.isNull = undefined;
          activityIDMMigratorContext.captureMessage(
            'Removed filter for "cloneOf" since the property does not exist on CogniteMaintenanceOrder data model',
            {
              level: 'warning',
              tags: {
                component: 'ActivityIDMMigrator',
                operation: 'mapFiltersInward',
              },
            }
          );
        }
      }
    });
    return newFilters;
  };
  mapSortInward = (sort: Sort): Sort => {
    return sort.map((x) => {
      if ('endTime' in x) {
        return { scheduledEndTime: x.endTime };
      }
      if ('actualEndTime' in x) {
        return { endTime: x.actualEndTime };
      }
      if ('startTime' in x) {
        return { scheduledStartTime: x.startTime };
      }
      if ('actualStartTime' in x) {
        return { startTime: x.actualStartTime };
      }
      return x;
    });
  };
}

export function createActivityIDMMigrator(
  ...args: ConstructorParameters<typeof ActivityIDMMigrator>
): ActivityViewMigrator {
  return new ActivityIDMMigrator(...args);
}
